import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import RadioBlock from '../../../../../../components/Form/RadioBlock'
import Button from '../../../../../../components/Button'
import AdPreviewSoundControlWrapper from '../../../../../../features/components/FacebookPreviews/reusablePreviewComponents/AdPreviewSoundControlWrapper'
import CarouselInstagramStoriesPreview from '../../../../../../features/components/FacebookPreviews/CarouselInstagramStoriesPreview'
import FacebookSingleMediaAdPreview from '../../../../../../features/components/FacebookPreviews/FacebookSingleMediaAdPreview'
import FacebookCarouselAdPreview from '../../../../../../features/components/FacebookPreviews/FacebookCarouselAdPreview'
import Field from '../../../../../../components/Form/Field'
import FieldLabelWrapper from '../../../../../../features/components/Form/FieldLabelWrapper'
import FacebookExistingPostPreview from '../../../../../../features/components/FacebookPreviews/FacebookExistingPostPreview'
import ToggleField from '../../../../../../components/Form/ToggleField'

import useGetInstagramAccounts from '../../../../../../hooks/instagramAccountsHooks/useGetInstagramAccounts'
import useFlatFormikValues from '../../../useFlatFormikValues'
import useLineItemDetails from '../hooks/useLineItemDetails'
import { formatOptionsList } from '../../../../../../features/formatters'

import {
  accessibleSocialPagesIsLoadingSelector,
  accessibleSocialPagesSelector
} from '../../../../../../modules/selectors/socialAccounts'

import {
  FB_PAGE_CHOICE,
  FB_PAGE_OPTION,
  FB_PAGE_SELECT,
  FB_SPONSOR_PAGE_ID,
  FORMAT_CAROUSEL,
  FORMAT_EXISTING_POST,
  FORMAT_OPTION,
  FORMAT_SINGLE,
  FORMAT_STORY,
  INCLUDE_PARTNERSHIP_PAGE,
  INSTA_ACCOUNT_CHOICE
} from '../../../fields'

import { AdFacebookCreateContext } from '../../../../formsContexts/AdFacebookCreateFormContextProvider'
import { LEAD_GENERATION } from '../../../../../../constants/campaigns'
import { PAGE_LIKES } from '../../../../../../constants/reusableObjectives'

import useStyles from '../../../../../../styles/common/stepForms'

const fields = [FB_PAGE_CHOICE, INSTA_ACCOUNT_CHOICE]

const FacebookPageStep = ({ formik, handleStepChange, isActive }) => {
  const classes = useStyles()

  const { t } = useTranslation()

  const { values, setFieldValue } = formik

  const { placementPositions } = useLineItemDetails()
  const { selectedInstagramAccounts } = useGetInstagramAccounts(values[FB_PAGE_CHOICE])
  const { isLoading: instagramAccountsIsLoading, results: instagramAccounts } = selectedInstagramAccounts

  const facebookPages = useSelector(accessibleSocialPagesSelector)
  const facebookPagesLoading = useSelector(accessibleSocialPagesIsLoadingSelector)

  const { lineItem } = useContext(AdFacebookCreateContext)
  const { objective: lineItemObjective } = lineItem

  const includePartnershipPage = values[INCLUDE_PARTNERSHIP_PAGE]

  const optionFormat = values[FORMAT_OPTION]
  const selectedFacebookPage = values[FB_PAGE_CHOICE]

  // const selectedFacebookPageName = useMemo(() => {
  //   return facebookPages.find(page => page.external_id === selectedFacebookPage)?.name
  // }, [facebookPages, selectedFacebookPage])

  const showPartnershipAdsToggle =
    optionFormat !== FORMAT_EXISTING_POST &&
    facebookPages.length > 1 &&
    // We don't support partnership ads for lineItems with objective 'page_likes'
    lineItemObjective !== PAGE_LIKES

  const formattedInstagramAccounts = useMemo(() => {
    return formatOptionsList({
      list: instagramAccounts,
      valueName: 'id',
      labelName: 'username'
    })
  }, [instagramAccounts])

  const formattedFacebookPages = formatOptionsList({
    list: facebookPages,
    valueName: 'external_id',
    labelName: 'name'
  })

  // Dropdown with instagram accounts should appear only if:
  // 1) /pages/{id}/instagram_accounts/ results > 1
  // 2) lineItem.placement_positions includes some item with 'instagram' string
  const showInstagramDropdown =
    instagramAccounts &&
    instagramAccounts.length > 1 &&
    placementPositions &&
    placementPositions.some(item => item.includes('instagram'))

  const handleContinue = useCallback(() => {
    handleStepChange(fields)
  }, [handleStepChange])

  const flatFormikValues = useFlatFormikValues({
    formikValues: values,
    allowUseSingleMediaItem: false
  })

  const partnershipAvailablePages = useMemo(() => {
    return formattedFacebookPages.filter(page => page.value !== selectedFacebookPage)
  }, [formattedFacebookPages, selectedFacebookPage])

  const renderPreview = useCallback(() => {
    // This step is reusable on 4 different routes, so we need to check the format option
    if (optionFormat === FORMAT_SINGLE) {
      return <FacebookSingleMediaAdPreview formikValues={flatFormikValues} elementInProgress={FB_PAGE_OPTION} />
    }

    if (optionFormat === FORMAT_CAROUSEL) {
      return <FacebookCarouselAdPreview formikValues={flatFormikValues} elementInProgress={FB_PAGE_OPTION} />
    }

    if (optionFormat === FORMAT_STORY) {
      return <CarouselInstagramStoriesPreview formikValues={flatFormikValues} elementInProgress={FB_PAGE_OPTION} />
    }

    if (optionFormat === FORMAT_EXISTING_POST) {
      return <FacebookExistingPostPreview values={values} isInProgress={true} />
    }
  }, [optionFormat, flatFormikValues, values])

  useEffect(() => {
    if (!includePartnershipPage) {
      // Reset sponsorPageId when turn off partnership toggle
      setFieldValue(FB_SPONSOR_PAGE_ID, null)
    }
  }, [includePartnershipPage, setFieldValue])

  return (
    <>
      <div className={classes.stepTitle}>{t('Please select a page to run the ad from')}</div>
      <div className={classes.stepBody}>
        <AdPreviewSoundControlWrapper formikValues={flatFormikValues} isSoundDisabled={!isActive}>
          {renderPreview()}
        </AdPreviewSoundControlWrapper>
        <RadioBlock
          setFieldValue={setFieldValue}
          id="radio_fb_page_option_select"
          name={FB_PAGE_OPTION}
          value={FB_PAGE_SELECT}
          selectedValue={values[FB_PAGE_OPTION]}
          label={t('Select Facebook Page')}
        >
          <Field
            placeholder={t('Facebook page')}
            formik={formik}
            name={FB_PAGE_CHOICE}
            options={formattedFacebookPages}
            isLoading={facebookPagesLoading}
            isMulti={false}
            isSearchable={false}
            // For lead generation lineItems we preselect facebook page from lineItem and do not allow change it
            isDisabled={lineItemObjective === LEAD_GENERATION}
          />
          {/*// todo this text should be shown only for app promotion campaigns, it's commented out until it's resolved*/}
          {/*{selectedFacebookPageName && <p>Forms submitted from your ad will go to {selectedFacebookPageName}.</p>}*/}
          <br />
          {showPartnershipAdsToggle && (
            <ToggleField
              name={INCLUDE_PARTNERSHIP_PAGE}
              setFieldValue={setFieldValue}
              isEnabled={includePartnershipPage}
              label="Partnership ad"
            />
          )}
          {includePartnershipPage && (
            <Field
              placeholder={t('Sponsor Facebook page')}
              formik={formik}
              name={FB_SPONSOR_PAGE_ID}
              options={partnershipAvailablePages}
              isLoading={facebookPagesLoading}
              isMulti={false}
              isSearchable={false}
            />
          )}
          {showInstagramDropdown && (
            <FieldLabelWrapper label={t('Select Instagram Account')}>
              <Field
                placeholder={t('Instagram account')}
                formik={formik}
                name={INSTA_ACCOUNT_CHOICE}
                options={formattedInstagramAccounts}
                isLoading={instagramAccountsIsLoading}
                isMulti={false}
                isSearchable={false}
              />
            </FieldLabelWrapper>
          )}
        </RadioBlock>
      </div>
      <div className={classes.stepFooter}>
        <Button type="button" className="dark" onClick={handleContinue}>
          {t('Continue')}
        </Button>
      </div>
    </>
  )
}

export default FacebookPageStep
